import { cva, type VariantProps } from "class-variance-authority";

/**
 * The one definition of button styling for the whole app.
 *
 * `<Button>` renders it, and the `form*ButtonClass` strings in formStyles.ts
 * are built from it, so a raw `<button>` / `<Link>` that can't become a
 * `<Button>` still gets identical styling. Pick a variant by what the action
 * does, not by the colour you want — see docs/THEME.md.
 *
 * Disabled: filled variants (primary, danger) turn neutral grey so a blocked
 * action never reads as "a lighter shade of the same button"; outline
 * variants fade.
 */
export const buttonVariants = cva(
  "inline-flex items-center justify-center font-medium transition-colors border rounded-control focus:outline-none disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        /** The main action on a page or form. One per view. */
        primary:
          "border-transparent text-white bg-primary-700 hover:bg-primary-800 disabled:bg-neutral-200 disabled:text-neutral-500 disabled:hover:bg-neutral-200 focus-visible:border-primary-950",
        /** Supporting actions: Edit, Export, Refresh. Brand-tinted outline. */
        secondary:
          "bg-white text-primary-700 border-primary-300 hover:bg-primary-50 disabled:opacity-50 focus-visible:border-primary-700",
        /** The way out of a form: Cancel, Back. */
        neutral:
          "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-100 disabled:opacity-50 focus-visible:border-primary-700",
        /** Lowest emphasis. */
        ghost:
          "border-transparent bg-neutral-100 text-neutral-800 hover:bg-primary-100 hover:text-primary-800 disabled:opacity-50 focus-visible:border-primary-700",
        /** Brand-tinted fill for small "+ Add …" actions beside a field. */
        soft:
          "text-primary-700 bg-primary-50 border-primary-200 hover:bg-primary-100 hover:border-primary-300 disabled:opacity-50 disabled:hover:bg-primary-50 focus-visible:border-primary-700",
        /** Confirms a destructive action (the Delete inside a confirm step). */
        danger:
          "border-transparent text-white bg-danger-600 hover:bg-danger-700 disabled:bg-neutral-200 disabled:text-neutral-500 disabled:hover:bg-neutral-200 focus-visible:border-danger-900",
        /** Starts a destructive action from a page (Delete Account, Remove). */
        dangerOutline:
          "bg-white text-danger-600 border-danger-300 hover:bg-danger-50 disabled:opacity-50 focus-visible:border-danger-500",
        /** Looks like a text link, behaves like a button. */
        link:
          "border-transparent text-link hover:text-link-hover hover:underline disabled:opacity-50 disabled:no-underline focus-visible:underline",
      },
      size: {
        sm: "gap-1.5 px-3 py-1.5 text-xs",
        md: "gap-2 px-4 py-2 text-sm",
        lg: "gap-2 px-5 py-2.5 text-base",
        /** Square, for icon-only buttons. Pair with an aria-label. */
        icon: "p-2 text-sm",
        /** No padding, for the `link` variant sitting inline with text. */
        inline: "gap-1 p-0 text-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
